import React from 'react';
import environments from 'src/env';
import { readJson } from 'src/core/engine/utils/readJson';
import { APP_ENV_STATE_ID } from 'src/core/engine/browser/initBrowserApollo';
import { scheduleIdle } from 'src/components/Scripts/scheduleIdle';

const appEnv = readJson(APP_ENV_STATE_ID);
const isMaster = appEnv.RELEASE_STAGE === 'production';

let bugsnagStarted = false;

async function startBugsnag() {
    if (bugsnagStarted || typeof window === 'undefined') return;
    bugsnagStarted = true;
    const [{ default: Bugsnag }, { default: BugsnagPluginReact }] = await Promise.all([
        import(/* webpackChunkName: "bugsnag" */ '@bugsnag/js'),
        import(/* webpackChunkName: "bugsnag" */ '@bugsnag/plugin-react'),
    ]);
    Bugsnag.start({
        apiKey: environments[isMaster ? 'master' : appEnv.RELEASE_STAGE].BUGSNAG_ID,
        enabledReleaseStages: ['production', 'staging'],
        releaseStage: appEnv.RELEASE_STAGE,
        plugins: [new BugsnagPluginReact()],
    });
    return Bugsnag;
}

export function reportReactError(error: unknown, componentStack?: string | null) {
    startBugsnag()
        .then(async () => {
            const Bugsnag = (await import(/* webpackChunkName: "bugsnag" */ '@bugsnag/js')).default;
            Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), (event) => {
                event.unhandled = true;
                event.severity = 'error';
                event.addMetadata('react', { componentStack });
            });
        })
        .catch(() => {
            /* reporting stays off */
        });
}

type ErrorBoundaryProps = { children: React.ReactNode };
type ErrorBoundaryState = { error: Error | null };

/**
 * Stays in the tree from the first render. Swapping in Bugsnag's own boundary once
 * it loads changes the element type above the app, and React remounts everything
 * under it (re-running every mount effect and `cache-and-network` query).
 * Mirrors Bugsnag's boundary: report the error, then render nothing.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { error: null };

    private cancelIdle?: () => void;

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    componentDidMount() {
        this.cancelIdle = scheduleIdle(() => {
            startBugsnag().catch(() => {
                /* reporting stays off */
            });
        }, 3000);
    }

    componentWillUnmount() {
        this.cancelIdle?.();
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        reportReactError(error, info.componentStack);
    }

    render() {
        return this.state.error ? null : this.props.children;
    }
}
